// The per-row diff of an app's write approval. Each row says what happens to
// it and, when the person may decide, carries one checkbox: unchecked rows are
// left out of the approval. Values are shown as written; an absent value is
// shown as empty, never as zero or blank text.
import { StatusPill, type StatusTone } from '@/components/ui/kit';
import type { AppApprovalsCopy } from '@/i18n/ns/app-approvals';
import type { ApprovalRow } from './app-decision-types';

export function RowDiff({
  copy,
  rows,
  excluded,
  disabled,
  selectable,
  onToggle,
}: {
  copy: AppApprovalsCopy;
  rows: readonly ApprovalRow[];
  excluded: ReadonlySet<string>;
  disabled: boolean;
  selectable: boolean;
  onToggle: (rowId: string) => void;
}) {
  const action = { create: copy.rowCreate, update: copy.rowUpdate, delete: copy.rowDelete };
  const tone: Record<ApprovalRow['action'], StatusTone> = {
    create: 'success',
    update: 'neutral',
    delete: 'danger',
  };
  const show = (value: string | null) =>
    value === null ? <em className="ws-row-diff-empty">{copy.fieldEmpty}</em> : value;
  return (
    <ul className="ws-row-diff">
      {rows.map((row) => {
        const left = excluded.has(row.rowId);
        return (
          <li
            key={row.rowId}
            className="ws-row-diff-row"
            data-action={row.action}
            data-excluded={left || undefined}
          >
            <div className="ws-row-diff-head">
              {selectable ? (
                <label className="ws-row-diff-include">
                  <input
                    type="checkbox"
                    checked={!left}
                    disabled={disabled}
                    onChange={() => onToggle(row.rowId)}
                    aria-label={`${copy.rowInclude}: ${row.title}`}
                  />
                  <span>{row.title}</span>
                </label>
              ) : (
                <strong>{row.title}</strong>
              )}
              <StatusPill tone={left ? 'neutral' : tone[row.action]}>
                {left ? copy.rowExcluded : action[row.action]}
              </StatusPill>
            </div>
            <dl className="ws-row-diff-fields">
              {row.changes.map((each) => (
                <div key={each.field} className="ws-row-diff-field">
                  <dt>{each.label}</dt>
                  <dd>
                    {row.action !== 'create' && (
                      <span className="ws-row-diff-before">
                        <span className="ws-sr-only">{copy.fieldBefore}: </span>
                        {show(each.before)}
                      </span>
                    )}
                    {row.action !== 'delete' && (
                      <span className="ws-row-diff-after">
                        <span className="ws-sr-only">{copy.fieldAfter}: </span>
                        {show(each.after)}
                      </span>
                    )}
                  </dd>
                </div>
              ))}
            </dl>
          </li>
        );
      })}
    </ul>
  );
}
